/**
 * Connection-operation overlay engine for optimistic connection mutations.
 *
 * The query cache holds pristine server truth (`base`); in-flight mutations are an
 * ordered operation log (TanStack Query's mutation cache); every consumer renders
 * `applyOperations(base, pendingOperations)`. Because application always starts from a
 * pristine base and never mutates in place, optimistic state composes and rolls back by
 * construction: an errored mutation drops its operation, a completed one folds server
 * truth into `base`. No inverse to compute, no position bookkeeping.
 *
 * Framework-agnostic: no Relay or event-bus dependency.
 */
import {createRecordDocumentAdapter, type RecordAdapterOptions, recordDocumentAdapter} from './record-document-adapter'
import type {
  ConnectionOperation,
  DocumentAdapter,
  DocumentAdapterContext,
  DocumentAdapterResult,
  EntityOperation,
  PendingConnectionOperation,
} from './operation-types'

// eslint-disable-next-line no-barrel-files/no-barrel-files -- Preserve the package's public type imports.
export type * from './operation-types'

// eslint-disable-next-line no-barrel-files/no-barrel-files -- Expose the default adapter through the same public entry point.
export {createRecordDocumentAdapter, type RecordAdapterOptions, recordDocumentAdapter}

// eslint-disable-next-line no-barrel-files/no-barrel-files -- Preserve public helper imports after extraction.
export {
  collectIdentities,
  type EntityFieldMapper,
  hasIncorporatedOperation,
  markOperationIncorporated,
  OPTIMISTIC_KEY,
  removeIncorporatedOperation,
  type SetFieldsMapper,
} from './record-document-adapter'

export function isEntityOperation(operation: ConnectionOperation): operation is EntityOperation {
  return (
    operation.type === 'setFields' ||
    operation.type === 'upsertEntityFieldItem' ||
    operation.type === 'removeEntityFieldItem' ||
    operation.type === 'deltaEntityField' ||
    operation.type === 'deltaEntityFieldValues' ||
    operation.type === 'updateEntityFieldItems'
  )
}

export function operationNeedsIncorporation(operation: ConnectionOperation): boolean {
  return (
    operation.type === 'deltaEntityField' ||
    operation.type === 'deltaEntityFieldValues' ||
    operation.type === 'updateEntityFieldItems' ||
    ('counts' in operation && Boolean(operation.counts?.length))
  )
}

/** Semantic mutation-cache path for operations that edit fields on a document node rather
 * than structurally changing one of its connections. */
export const DOCUMENT_FIELDS_PATH = ['documentFields']

export type DocumentAdapterOptions<T> =
  | ({documentAdapter: DocumentAdapter<T>} & {[K in keyof RecordAdapterOptions]?: never})
  | (RecordAdapterOptions & {documentAdapter?: never})

export type ApplyOptions<T = unknown> = DocumentAdapterOptions<T> & DocumentAdapterContext & {operationId?: string}

/**
 * A collision-free key for a connection path. `join(delimiter)` is lossy (`[]` and `['']`
 * both collapse to `''`, and a delimiter inside a segment aliases a different structure);
 * JSON encodes the array structure and each segment exactly.
 */
export function connectionPathKey(path: readonly string[]): string {
  return JSON.stringify(path)
}

export function resolveDocumentAdapter<T>(options: DocumentAdapterOptions<T>): DocumentAdapter<T> {
  if (options.documentAdapter) return options.documentAdapter
  if (
    options.identityPath ||
    options.mapFields ||
    options.mapEntityField ||
    options.applyEntityFieldItem ||
    options.applyEntityFieldValuesDelta ||
    options.updateEntityFieldItems
  ) {
    return createRecordDocumentAdapter(options)
  }
  return recordDocumentAdapter
}

export function applyDocumentOperation<T>(
  document: T,
  change: PendingConnectionOperation,
  documentAdapter: DocumentAdapter<T>,
  context: DocumentAdapterContext = {},
): DocumentAdapterResult<T> {
  return documentAdapter(document, change, context)
}

/**
 * Applies one operation to `doc`, returning a new document (immer structural sharing —
 * unrelated cache entries keep their identity). Pure: never mutates its input.
 */
export function applyOperation<T>(
  doc: T,
  ownerId: string,
  operation: ConnectionOperation,
  options: ApplyOptions<NoInfer<T>> = {},
): T {
  const result = applyDocumentOperation(
    doc,
    {ownerId, operation, operationId: options.operationId},
    resolveDocumentAdapter(options),
    {incorporateOperation: options.incorporateOperation},
  )
  return result ? result.document : doc
}

/**
 * Applies every pending operation in dispatch order onto the pristine `base` — what a
 * consumer renders; see `useOptimisticData`.
 */
export function applyOperations<T>(
  base: T,
  pendingOperations: readonly PendingConnectionOperation[],
  options: ApplyOptions<NoInfer<T>> = {},
): T {
  const documentAdapter = resolveDocumentAdapter(options)
  return pendingOperations.reduce((document, change) => {
    const result = applyDocumentOperation(document, change, documentAdapter, {
      incorporateOperation: options.incorporateOperation,
    })
    return result ? result.document : document
  }, base)
}
